import { Dialog, Transition } from "@headlessui/react";
import React from "react";
import LoadingIcon from "./loadingicon";
import { classNames } from "../utils/classnames";

export enum ZenDialogState {
  Invalid,
  Displaying,
  Submitting,
}

export const ZenDialog: React.FC<
  {
    show?: boolean;
    state?: ZenDialogState;
    onCancel: () => void;
    onSubmit: () => void;
    icon: React.ForwardRefExoticComponent<
      React.PropsWithoutRef<React.SVGProps<SVGSVGElement>> & {
        title?: string;
        titleId?: string;
      } & React.RefAttributes<SVGSVGElement>
    >;
    title: React.ReactFragment;
    cancel?: React.ReactFragment;
    submit: React.ReactFragment;
    initialFocus?: React.MutableRefObject<HTMLElement | null>;
  } & React.PropsWithChildren
> = ({
  show,
  state,
  onCancel,
  onSubmit,
  icon,
  title,
  children,
  cancel,
  submit,
  initialFocus,
}) => {
  const Icon = icon;
  const cancelText = cancel ?? "Cancel";
  const loadingState = state ?? ZenDialogState.Displaying;

  return (
    <Transition show={show} as={React.Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={onCancel}
        initialFocus={initialFocus}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className="inline-grid align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all gap-1 sm:my-8 sm:align-middle sm:max-w-xl sm:w-full sm:p-6"
              style={{
                gridTemplateAreas: `"icon title" ". content" "buttons buttons"`,
                gridTemplateColumns: "auto 1fr",
                gridTemplateRows: "auto 1fr auto",
                maxHeight: "calc(100vh - 4rem)",
              }}
            >
              <div
                style={{ gridArea: "icon" }}
                className="mx-auto flex items-center justify-center h-10 w-10 rounded-full bg-indigo-100"
              >
                <Icon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
              </div>
              <div
                className="p-1"
                style={{
                  gridArea: "title",
                  alignSelf: "center",
                }}
              >
                <Dialog.Title
                  as="h3"
                  className="text-lg leading-6 font-medium text-gray-900"
                >
                  {title}
                </Dialog.Title>
              </div>
              <div
                style={{ gridArea: "content" }}
                className="text-sm text-gray-500 overflow-y-auto p-1"
              >
                {children}
              </div>
              <div
                style={{ gridArea: "buttons" }}
                className="sm:flex sm:flex-row-reverse p-1"
              >
                <button
                  type="button"
                  className={classNames(
                    "w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm",
                    loadingState === ZenDialogState.Invalid
                      ? "bg-gray-300 text-gray-400"
                      : "bg-indigo-600 hover:bg-indigo-700 text-white"
                  )}
                  disabled={
                    loadingState === ZenDialogState.Submitting ||
                    loadingState === ZenDialogState.Invalid
                  }
                  onClick={() => onSubmit()}
                >
                  {loadingState === ZenDialogState.Submitting && (
                    <LoadingIcon />
                  )}
                  {submit}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  disabled={loadingState === ZenDialogState.Submitting}
                  onClick={() => onCancel()}
                >
                  {cancelText}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
